<template>
    <div id="card-information__container" class="tw-w-full">
        <form @submit="formWillSubmit">
            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-3/5 tw-pb-2">
                    <h4 :class="{ 'tw-text-gray-800': updatingCard, 'tw-text-white': !updatingCard, 'tw-mb-3': updatingCard }">
                        {{ title }}
                        <span v-if="isOptional">(Optional)</span>
                    </h4>
                </div>
            </div>

            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-3/5">
                    <div>
                        <input
                            type="text"
                            class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-bg-transparent tw-rounded"
                            :class="{
                                'tw-text-gray-800': updatingCard,
                                'tw-text-white': !updatingCard,
                                'tw-border-red-500': showCardholderNameError
                            }"
                            :placeholder="__('purchase.cardholder_name_placeholder')"
                            v-model="cardholderName"
                            @blur="fieldTouched.cardholder_name = true"
                            @input="fieldTouched.cardholder_name = true"
                        />
                        <span v-show="showCardholderNameError" class="tw-text-red-600 tw-mt-1 tw-block">
                            {{ errors.cardholder_name }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="tw-flex tw-justify-center tw-mt-3">
                <div class="tw-w-full md:tw-w-3/5">
                    <div class="tw-flex tw-flex-wrap">
                        <div class="tw-w-5/6">
                            <div>
                                <input
                                    type="text"
                                    id="purchase__credit-card-number-input"
                                    class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-bg-transparent tw-rounded"
                                    :class="{
                                        'tw-text-gray-800': updatingCard,
                                        'tw-text-white': !updatingCard,
                                        'tw-border-red-500': showCardNumberError
                                    }"
                                    :placeholder="__('purchase.card_number_placeholder')"
                                    v-model="cardNumber"
                                    @blur="handleCardNumberBlur"
                                    @input="handleCardNumberInput"
                                />
                                <span v-show="showCardNumberError" class="tw-text-red-600 tw-mt-1 tw-block">
                                    {{ errors.card_number }}
                                </span>
                            </div>
                        </div>
                        <div class="tw-pl-1 tw-w-1/6 tw-flex tw-items-center tw-justify-center">
                            <img :src="asset(cardInformation.cardTypeImage)" class="tw-max-h-8" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-3/5">
                    <div class="tw-flex tw-flex-wrap">
                        <div class="tw-w-full md:tw-w-1/2 tw-pr-2">
                            <div>
                                <input
                                    type="text"
                                    class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-bg-transparent tw-rounded tw-mt-3"
                                    :class="{
                                        'tw-text-gray-800': updatingCard,
                                        'tw-text-white': !updatingCard,
                                        'tw-border-red-500': showExpiryError
                                    }"
                                    :placeholder="__('purchase.expiry_placeholder')"
                                    v-model="expiry"
                                    @blur="fieldTouched.expiry = true"
                                    @input="fieldTouched.expiry = true"
                                />
                                <span v-show="showExpiryError" class="tw-text-red-600 tw-mt-1 tw-block">
                                    {{ errors.expiry }}
                                </span>
                            </div>
                        </div>

                        <div class="tw-w-full md:tw-w-1/2 tw-pl-2">
                            <div>
                                <input
                                    type="text"
                                    class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-bg-transparent tw-rounded tw-mt-3"
                                    :class="{
                                        'tw-text-gray-800': updatingCard,
                                        'tw-text-white': !updatingCard,
                                        'tw-border-red-500': showCvvError
                                    }"
                                    :placeholder="__('purchase.cvv_placeholder')"
                                    v-model="cvv"
                                    @blur="fieldTouched.cvv = true"
                                    @input="fieldTouched.cvv = true"
                                />
                                <span v-show="showCvvError" class="tw-text-red-600 tw-mt-1 tw-block">
                                    {{ errors.cvv }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="updatingCard" class="tw-w-full tw-text-center tw-mt-3">
                <button
                    class="tw-px-4 tw-py-2 tw-rounded tw-border tw-mt-3 tw-transition-colors tw-duration-200"
                    :class="updatingCard ? 
                        'tw-border-blue-500 tw-text-blue-500 hover:tw-bg-blue-500 hover:tw-text-white' : 
                        'tw-border-white tw-text-white hover:tw-bg-white hover:tw-text-gray-800'"
                    @click.prevent="formWillSubmit"
                >
                    {{ __('purchase.submit_btn_text') }}
                </button>
            </div>
        </form>

    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, getCurrentInstance } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";

interface CardInformation {
    number: string | null;
    last12Digits: string | null;
    firstFourDigits: string | null;
    lastFourDigits: string | null;
    cardholderName: string | null;
    expiry: string | null;
    cvv: string | null;
    cardType: string | null;
    cardTypeImage: string;
}

interface Props {
    title: string;
    updatingCard?: boolean;
    isOptional?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    updatingCard: false,
    isOptional: false,
});

const emit = defineEmits<{
    "previous-component": [];
    "card-information-did-submit": [cardInformation: CardInformation];
}>();

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;
const asset = instance?.appContext.config.globalProperties.asset;

// Track which fields have been touched by the user
const fieldTouched = reactive({
    cardholder_name: false,
    card_number: false,
    expiry: false,
    cvv: false,
});

// Card information object
const cardInformation = reactive<CardInformation>({
    number: null,
    last12Digits: null,
    firstFourDigits: null,
    lastFourDigits: null,
    cardholderName: null,
    expiry: null,
    cvv: null,
    cardType: null,
    cardTypeImage: "/marketing/smb/payment/other.png",
});

// Create validation schema
const validationSchema = yup.object({
    cardholder_name: yup
        .string()
        .test("required", __('purchase.validation.required', { field: __('purchase.field_names.cardholder_name') }), (value) => {
            if (props.isOptional) return true;
            return !!value;
        })
        .matches(/^[a-zA-Z ]*$/, __('purchase.validation.alphabetic_only', { field: __('purchase.field_names.cardholder_name') })),
    card_number: yup
        .string()
        .test("required", __('purchase.validation.required', { field: __('purchase.field_names.card_number') }), (value) => {
            if (props.isOptional) return true;
            return !!value;
        })
        .test("card-length", __('purchase.validation.min_characters', { field: __('purchase.field_names.card_number'), min: 16 }), (value) => {
            if (!value) return props.isOptional;
            const digitsOnly = value.replace(/\D/g, '');
            return digitsOnly.length === 16;
        }),
    expiry: yup
        .string()
        .test("required", __('purchase.validation.required', { field: __('purchase.field_names.expiry_date') }), (value) => {
            if (props.isOptional) return true;
            return !!value;
        })
        .test("date-format", __('purchase.validation.date_format_mmyy', { field: __('purchase.field_names.expiry_date') }), (value) => {
            if (!value) return props.isOptional;
            const regex = /^(0[1-9]|1[0-2])([0-9]{2})$/;
            return regex.test(value);
        }),
    cvv: yup
        .string()
        .test("required", __('purchase.validation.required', { field: __('purchase.field_names.cvv') }), (value) => {
            if (props.isOptional) return true;
            return !!value;
        })
        .test("cvv-format", __('purchase.validation.min_characters', { field: __('purchase.field_names.cvv'), min: 3 }), (value) => {
            if (!value) return props.isOptional;
            const regex = /^[0-9]{3}$/;
            return regex.test(value);
        }),
});

// Setup form validation 
const { handleSubmit, errors } = useForm({
    validationSchema,
    validateOnMount: false,
});

// Use field for each form field
const { value: cardholderName } = useField<string>("cardholder_name");
const { value: cardNumber } = useField<string>("card_number");
const { value: expiry } = useField<string>("expiry");
const { value: cvv } = useField<string>("cvv");

// Computed properties to control when errors should be shown
const showCardholderNameError = computed(() => fieldTouched.cardholder_name && errors.value.cardholder_name);
const showCardNumberError = computed(() => fieldTouched.card_number && errors.value.card_number);
const showExpiryError = computed(() => fieldTouched.expiry && errors.value.expiry);
const showCvvError = computed(() => fieldTouched.cvv && errors.value.cvv);

// Credit card type detection
const getCreditCardType = () => {
    let cardNum = null;

    if (!cardNumber.value) {
        cardInformation.cardType = "other";
        cardInformation.cardTypeImage = "/marketing/smb/payment/other.png";
        return;
    }

    cardNum = parseInt(cardNumber.value);
    // Check for MasterCard
    if (/^5[1-5]/.test(String(cardNum)) && String(cardNum).length <= 16) {
        cardInformation.cardType = "mastercard";
        cardInformation.cardTypeImage = "/marketing/smb/payment/mastercard.png";
    } 
    // Check for Visa
    else if (/^4/.test(String(cardNum)) && String(cardNum).length <= 16) {
        cardInformation.cardType = "visa";
        cardInformation.cardTypeImage = "/marketing/smb/payment/visa.png";
    } 
    // Check for Amex
    else if (/^3[47]/.test(String(cardNum)) && String(cardNum).length <= 15) {
        cardInformation.cardType = "amex";
        cardInformation.cardTypeImage = "/marketing/smb/payment/amex.png";
    } 
    else {
        cardInformation.cardType = "other";
        cardInformation.cardTypeImage = "/marketing/smb/payment/other.png";
    }
};

// Handle card number input events
const handleCardNumberInput = () => {
    fieldTouched.card_number = true;
    getCreditCardType();
};

const handleCardNumberBlur = () => {
    fieldTouched.card_number = true;
    getCreditCardType();
};

// Form submission handler
const formWillSubmit = handleSubmit((values) => {
    // Update cardInformation with form values
    cardInformation.cardholderName = values.cardholder_name;
    cardInformation.number = values.card_number;
    cardInformation.expiry = values.expiry;
    cardInformation.cvv = values.cvv;
    cardInformation.firstFourDigits = String(values.card_number).substring(0, 4);
    cardInformation.lastFourDigits = String(values.card_number).slice(-4);
    
    emit("card-information-did-submit", cardInformation);
}, () => {
    // Mark all fields as touched so errors show when validation fails
    fieldTouched.cardholder_name = true;
    fieldTouched.card_number = true;
    fieldTouched.expiry = true;
    fieldTouched.cvv = true;
    
    // Handle validation errors - scroll to error
    const errorElement = document.querySelector(".tw-border-red-500");
    if (errorElement) {
        errorElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
});

// Back button handler
const backBtnDidClick = () => {
    emit("previous-component");
};

// Clear method
const clear = () => {
    cardInformation.number = null;
    cardInformation.last12Digits = null;
    cardInformation.firstFourDigits = null;
    cardInformation.lastFourDigits = null;
    cardInformation.cardholderName = null;
    cardInformation.expiry = null;
    cardInformation.cvv = null;
    cardInformation.cardType = null;
    cardInformation.cardTypeImage = "/marketing/smb/payment/other.png";
    
    // Reset form fields
    cardholderName.value = "";
    cardNumber.value = "";
    expiry.value = "";
    cvv.value = "";
    
    // Reset touched states
    fieldTouched.cardholder_name = false;
    fieldTouched.card_number = false;
    fieldTouched.expiry = false;
    fieldTouched.cvv = false;
};

// Expose methods for parent component access
defineExpose({
    backBtnDidClick,
    formWillSubmit,
    clear,
});</script>

